import { MutableRefObject, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import type { YMap, YMapMarker } from '@yandex/ymaps3-types';

import { MapPoint, YandexMapCoorinates } from './YandexMap.types';

type InitProps = {
	containerRef: MutableRefObject<HTMLDivElement | null>;
	center: YandexMapCoorinates;
	zoom: number;
	bounds?: YandexMapCoorinates[];
	boundsMargin: number;
	markers?: MapPoint[];
	markersRef: MutableRefObject<YMapMarker[]>;
	mapRef: MutableRefObject<YMap | null>;
};

export const setMapLocation = ({
	map,
	center,
	zoom,
	bounds,
	boundsMargin,
}: {
	map: YMap;
	center: YandexMapCoorinates;
	zoom: number;
	bounds?: YandexMapCoorinates[];
	boundsMargin: number;
}) => {
	if (bounds && bounds.length > 1) {
		const longitudes = bounds.map(({ longitude }) => longitude);
		const latitudes = bounds.map(({ latitude }) => latitude);
		const west = Math.min(...longitudes);
		const east = Math.max(...longitudes);
		const south = Math.min(...latitudes);
		const north = Math.max(...latitudes);

		map.setMargin([boundsMargin, boundsMargin, boundsMargin, boundsMargin]);
		map.setLocation({
			bounds: [
				[west, south],
				[east, north],
			],
		});
		return;
	}

	map.setMargin([0, 0, 0, 0]);
	map.setLocation({ center: [center.longitude, center.latitude], zoom });
};

type MarkerElement = HTMLDivElement & { popupCleanup?: () => void };
type MarkerCleanup = () => void;

const markerCleanup = new WeakMap<YMapMarker, MarkerCleanup>();

export const useInitYaMap = ({
	center,
	markersRef,
	mapRef,
	containerRef,
	zoom,
	bounds,
	boundsMargin,
	markers = [],
}: InitProps) => {
	const latestMarkersRef = useRef(markers);
	latestMarkersRef.current = markers;

	useEffect(() => {
		let map: YMap | null = null;
		let cancelled = false;
		let container: HTMLDivElement | null = null;
		let wheelHandler: ((event: WheelEvent) => void) | null = null;

		const init = async () => {
			try {
				await ymaps3.ready;
				if (cancelled || !containerRef.current) return;

				const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker, YMapControls } =
					ymaps3;

				container = containerRef.current;
				map = new YMap(
					container,
					{
						location: {
							center: [center.longitude, center.latitude],
							zoom: zoom,
						},
						behaviors: ['drag', 'dblClick', 'pinchZoom'],
					},
					[new YMapDefaultSchemeLayer({}), new YMapDefaultFeaturesLayer({})],
				);
				mapRef.current = map;
				setMapLocation({ map, center, zoom, bounds, boundsMargin });

				try {
					const uiTheme = await import('@yandex/ymaps3-default-ui-theme');
					if (cancelled || !map) return;
					const controls = new YMapControls({ position: 'left' });
					controls.addChild(new uiTheme.YMapZoomControl({}));

					map.addChild(controls);
				} catch (err) {
					console.warn('Не удалось подключить UI theme', err);
				}

				if (cancelled || !map) return;
				addMarkers({ map, markers: latestMarkersRef.current, markersRef, YMapMarker });

				wheelHandler = (e: WheelEvent) => {
					if (!e.ctrlKey) return;

					e.preventDefault();
					const delta = Math.sign(e.deltaY);

					const currentZoom = (map as any).zoom ?? zoom;
					let newZoom = currentZoom - delta;
					if (newZoom < 0) newZoom = 0;
					if (newZoom > 25) newZoom = 25;

					map.setLocation({ zoom: newZoom, duration: 150 });
				};

				container.addEventListener('wheel', wheelHandler, { passive: false });
			} catch (error) {
				if (!cancelled) console.error('Ошибка при инициализации карты.', error);
			}
		};

		init();

		return () => {
			cancelled = true;
			if (container && wheelHandler) container.removeEventListener('wheel', wheelHandler);
			markersRef.current.forEach((marker) => {
				markerCleanup.get(marker)?.();
				markerCleanup.delete(marker);
			});
			markersRef.current = [];
			if (mapRef.current === map) mapRef.current = null;
			map?.destroy?.();
		};
	}, []);
};

export const addMarkers = ({
	markers,
	map,
	markersRef,
	YMapMarker,
}: {
	markers: MapPoint[];
	map: YMap;
	markersRef: MutableRefObject<YMapMarker[]>;
	YMapMarker: any;
}) => {
	markersRef.current.forEach((m) => {
		markerCleanup.get(m)?.();
		markerCleanup.delete(m);
		map.removeChild(m);
	});
	markersRef.current = [];

	markers.forEach((marker) => {
		const el = createMarkerElement(marker.color ?? '#14489d', marker.tooltip);

		const mapMarker = new YMapMarker(
			{
				coordinates: [marker.longitude, marker.latitude],
				zIndex: 2,
			},
			el,
		);

		map.addChild(mapMarker);
		if (el.popupCleanup) markerCleanup.set(mapMarker, el.popupCleanup);
		markersRef.current.push(mapMarker);
	});
};

export const createMarkerElement = (color: string = 'red', popup?: React.ReactNode) => {
	const wrapper = document.createElement('div') as MarkerElement;
	wrapper.style.position = 'relative';
	wrapper.style.cursor = 'pointer';

	const dot = document.createElement('div');
	dot.className = 'ymaps-custom-marker';
	dot.style.background = color;

	wrapper.appendChild(dot);

	if (!popup) return wrapper;

	const popupEl = document.createElement('div');
	popupEl.className = 'ymap-custom-popup';
	popupEl.style.position = 'absolute';
	popupEl.style.bottom = '24px';
	popupEl.style.left = '50%';
	popupEl.style.transform = 'translateX(-50%)';
	popupEl.style.background = 'rgba(0,0,0,0.75)';
	popupEl.style.borderRadius = '6px';
	popupEl.style.boxShadow = '0px 2px 10px rgba(0,0,0,0.25)';
	popupEl.style.zIndex = '10';
	popupEl.style.display = 'none';

	const root = createRoot(popupEl);
	root.render(popup);

	wrapper.appendChild(popupEl);

	let opened = false;
	let closeOnOutside: ((ev: MouseEvent) => void) | null = null;

	const togglePopup = () => {
		opened = !opened;
		popupEl.style.display = opened ? 'block' : 'none';
		popupEl.className = opened ? 'popup-opened' : '';

		if (opened) {
			closeOnOutside = (ev: MouseEvent) => {
				if (!wrapper.contains(ev.target as Node)) {
					popupEl.style.display = 'none';
					popupEl.className = '';
					opened = false;
					if (closeOnOutside) window.removeEventListener('click', closeOnOutside);
				}
			};

			window.addEventListener('click', closeOnOutside);
		}
	};

	wrapper.addEventListener('click', togglePopup);

	wrapper.popupCleanup = () => {
		wrapper.removeEventListener('click', togglePopup);
		if (closeOnOutside) window.removeEventListener('click', closeOnOutside);
		root.unmount();
	};

	return wrapper;
};
